import Head from 'next/head';
import Image, { type ImageLoaderProps, type ImageProps } from 'next/image';

import CloudImageLoader from './loaders/cloudimage';
import { type ContentfulImageParams, ContentfulLoader } from './loaders/contentful';
import postFixUrl from './utils/postFixUrl';

export type ImageSizesConfig<Breakpoint extends string> = {
	[key in Breakpoint]: {
		imageWidth: string;
		trimming: [number, number] | [number, undefined] | [undefined, number]; // [width, height]
	};
};

export interface ImageComponentProps<Breakpoint extends string = string> extends ImageProps {
	/** Using CloudImage or Contentful */
	useLoader?: 'CloudImage' | 'Contentful' | false;
	/** Additional params you can pass when using Contentful loader */
	contentfulParams?: ContentfulImageParams;
	/** Rotation of the image */
	rotationDegrees?: number;
	/** Trim width and height numbers */
	trim?: ImageSizesConfig<Breakpoint>[Breakpoint]['trimming'];
	/** Only pass if you want to upscale an image @defaults to @false */
	upscale?: boolean;
}

type Props = Omit<ImageComponentProps, 'useLoader'> &
	(
		| { useLoader?: 'CloudImage' | false; contentfulParams?: never }
		| { useLoader?: 'Contentful'; contentfulParams?: ContentfulImageParams; upscale?: never }
	);

const ImageComponent = ({
	useLoader = 'CloudImage',
	rotationDegrees,
	trim,
	src,
	contentfulParams,
	upscale = true,
	...rest // Using rest here since all attributes of an img element can be passed through
}: Props): JSX.Element => {
	let preconnectDomain;
	switch (useLoader) {
		case false:
			rest.loader = undefined;
			break;
		case 'Contentful':
			rest.loader = (props: ImageLoaderProps): string => ContentfulLoader(props, contentfulParams);

			preconnectDomain = process.env.NEXT_PUBLIC_CONTENTFUL_IMAGES_URL;
			break;
		default:
			rest.loader = ({ quality, src, width }: ImageLoaderProps): string =>
				CloudImageLoader({ quality, rotationDegrees, src, trim, width, upscale });
			preconnectDomain = `https://${process.env.NEXT_PUBLIC_CLOUDIMG_IO_TOKEN}.cloudimg.io`;
			break;
	}

	return (
		<>
			{preconnectDomain && useLoader && (
				<Head>
					<link rel="preconnect" key={`${useLoader}-preconnect`} href={preconnectDomain} />
					<link rel="dns-prefetch" key={`${useLoader}-dns-prefetch`} href={preconnectDomain} />
				</Head>
			)}
			<Image loader={rest.loader} src={typeof src === 'string' ? postFixUrl(src) : src} {...rest} />
		</>
	);
};

export default ImageComponent;
